import { memo } from 'react';

const money = (value) =>
  Number(value || 0).toLocaleString('es-PE', { style: 'currency', currency: 'PEN' });

const toneClasses = {
  brand: {
    card: 'border-red-200 bg-white',
    icon: 'border-red-100 bg-red-50 text-red-600',
    bar: 'bg-red-500',
  },
  warning: {
    card: 'border-amber-200 bg-amber-50/40',
    icon: 'border-amber-100 bg-amber-100 text-amber-700',
    bar: 'bg-amber-500',
  },
  success: {
    card: 'border-emerald-200 bg-emerald-50/40',
    icon: 'border-emerald-100 bg-emerald-100 text-emerald-700',
    bar: 'bg-emerald-500',
  },
  neutral: {
    card: 'border-slate-200 bg-white',
    icon: 'border-slate-200 bg-slate-100 text-slate-600',
    bar: 'bg-slate-300',
  },
};

function Card({ title, value, icon, hint, tone = 'neutral', featured = false }) {
  const styles = toneClasses[tone] || toneClasses.neutral;

  return (
    <article
      className={[
        'rounded-2xl border p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md',
        styles.card,
        featured ? 'md:p-6' : '',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">{title}</p>
          <p className={['mt-2 font-bold text-slate-900', featured ? 'text-2xl' : 'text-xl'].join(' ')}>
            {value}
          </p>
          <p className="mt-2 text-xs text-slate-500">{hint || ' '}</p>
        </div>
        <div className={['rounded-xl border p-2', styles.icon, '[&_svg]:h-4 [&_svg]:w-4'].join(' ')}>
          {icon}
        </div>
      </div>
      <div className={['mt-4 h-1 w-14 rounded-full', styles.bar].join(' ')} />
    </article>
  );
}

function Section({ title, columns, cards }) {
  return (
    <section className="space-y-3">
      <div className="flex flex-col gap-1">
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
      </div>

      <div className={columns}>
        {cards.map((card) => (
          <Card key={card.title} {...card} />
        ))}
      </div>
    </section>
  );
}

const KPICards = ({ totals }) => {
  const {
    capital = 0,
    capitalPromedio = 0,
    totalPagar = 0,
    totalPagarPendiente = 0,
    capitalPendiente = 0,
    renta = 0,
    rentaPendiente = 0,
    prestamos = 0,
    clientesNuevosConPrestamo = 0,
    rentaEjecutada = 0,
    tasaPromedio = 0,
    prestamosInactivos = 0,
  } = totals || {};

  const carteraPendiente = [
    {
      title: 'Desembolso total',
      value: money(capital),
      hint: `${prestamos} préstamos registrados`,
      tone: 'brand',
      featured: true,
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    {
      title: 'Capital pendiente',
      value: money(capitalPendiente),
      hint: 'Capital por amortizar',
      tone: 'warning',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m0 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm5-5h.01"
          />
        </svg>
      ),
    },
    {
      title: 'Renta pendiente por cobrar',
      value: money(rentaPendiente),
      hint: 'Interés aún no pagado',
      tone: 'warning',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
          />
        </svg>
      ),
    },
    {
      title: 'Total pendiente de cobro',
      value: money(totalPagarPendiente),
      hint: 'Capital + renta pendientes',
      tone: 'warning',
      featured: true,
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
  ];

  const resumenFinanciero = [
    {
      title: 'Total a pagar',
      value: money(totalPagar),
      hint: 'Capital + intereses generados',
      tone: 'neutral',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      ),
    },
    {
      title: 'Renta total',
      value: money(renta),
      hint: 'Interés acumulado',
      tone: 'neutral',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
          />
        </svg>
      ),
    },
    {
      title: 'Renta ejecutada',
      value: money(rentaEjecutada),
      hint: 'Renta ya cobrada',
      tone: 'success',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    {
      title: 'Tasa promedio',
      value: `${Number(tasaPromedio).toFixed(2)}%`,
      hint: 'Interés medio aplicado',
      tone: 'neutral',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      ),
    },
  ];

  const actividadComercial = [
    {
      title: 'N. préstamos',
      value: prestamos,
      hint: 'Operaciones del periodo',
      tone: 'neutral',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
    },
    {
      title: 'Ticket promedio',
      value: money(capitalPromedio),
      hint: 'Promedio por préstamo',
      tone: 'neutral',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
      ),
    },
    {
      title: 'Clientes nuevos',
      value: clientesNuevosConPrestamo,
      hint: 'Modalidad NUEVO',
      tone: 'success',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
          />
        </svg>
      ),
    },
    {
      title: 'MODALIDAD INACTIVO',
      value: prestamosInactivos,
      hint: 'Clientes reenganchados',
      tone: 'warning',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M18.364 5.636l-12.728 12.728M8 8h.01M16 16h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
  ];

  return (
    <div className="mt-3 space-y-6">
      <Section
        title="Cartera y recuperación"
        columns="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4"
        cards={carteraPendiente}
      />

      <Section
        title="Panorama financiero"
        columns="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4"
        cards={resumenFinanciero}
      />

      <Section
        title="Indicadores comerciales"
        columns="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4"
        cards={actividadComercial}
      />
    </div>
  );
};

export default memo(KPICards);
